import { useState, useCallback, useEffect } from 'react';
import { 
    Dialog, 
    Button, 
    CircularProgress,
    IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';

const BranchEditModal = ({ open, onClose, onSave, isSaving = false, branchData = null }) => {

    const [formData, setFormData] = useState({
        Id: '',
        Code: '',
        Name: '',
        AddressLine1: '',
        AddressLine2: '',
        Location: '',
        PinCode: '',
        StateCodeId: '',
        ContactNumber1: '',
        ContactNumber2: '',
        ContactNumber3: '',
        Email: '',
        VATNumber: '',
        PanNumber: '',
        ImporterCode: '',
        TradeLicenseNo: '',
        PaymentBankId: '',
        BillPrefix: '',
        // BToCPrefix: '',
        // IGSTPrefix: '',
        // IGSTCustomerPrefix: '',
        CashOpeningBalance: '',
        CashOpeningDate: '',
        StockOpeningBalance: '',
        StockOpeningDate: '',
        IsEInvoicingEnabled: false,
        IsEWaybillEnabled: false,
        EinvoicePortalUserName: '',
        EinvoicePortalPassword: '',
        myGSTcafeCustomerID: '',
        myGSTcafeApiID: '',
        myGSTcafeApiSecret: ''
    });

    const [validationErrors, setValidationErrors] = useState({});

    useEffect(() => {
        if (branchData && open) {
            setFormData({
                Id: branchData.Id || '',
                Code: branchData.Code || '',
                Name: branchData.Name || '',
                AddressLine1: branchData.AddressLine1 || '',
                AddressLine2: branchData.AddressLine2 || '',
                Location: branchData.Location || '',
                PinCode: branchData.PinCode || '',
                StateCodeId: branchData.StateCodeId || '',
                ContactNumber1: branchData.ContactNumber1 || '',
                ContactNumber2: branchData.ContactNumber2 || '',
                ContactNumber3: branchData.ContactNumber3 || '',
                Email: branchData.Email || '',
                VATNumber: branchData.VATNumber || '',
                PanNumber: branchData.PanNumber || '',
                ImporterCode: branchData.ImporterCode || '',
                TradeLicenseNo: branchData.TradeLicenseNo || '',
                PaymentBankId: branchData.PaymentBankId || '',
                BillPrefix: branchData.BillPrefix || '',
                // BToCPrefix: branchData.BToCPrefix || '',
                // IGSTPrefix: branchData.IGSTPrefix || '',
                //IGSTCustomerPrefix: branchData.IGSTCustomerPrefix || '',
                CashOpeningBalance: branchData.CashOpeningBalance || '',
                CashOpeningDate: branchData.CashOpeningDate ? branchData.CashOpeningDate.split('T')[0] : '',
                StockOpeningBalance: branchData.StockOpeningBalance || '',
                StockOpeningDate: branchData.StockOpeningDate ? branchData.StockOpeningDate.split('T')[0] : '',
                IsEInvoicingEnabled: branchData.IsEInvoicingEnabled || false,
                IsEWaybillEnabled: branchData.IsEWaybillEnabled || false,
                EinvoicePortalUserName: branchData.EinvoicePortalUserName || '',
                EinvoicePortalPassword: branchData.EinvoicePortalPassword || '',
                myGSTcafeCustomerID: branchData.myGSTcafeCustomerID || '',
                myGSTcafeApiID: branchData.myGSTcafeApiID || '',
                myGSTcafeApiSecret: branchData.myGSTcafeApiSecret || ''
            });
        }
    }, [branchData, open]);

    const validateForm = useCallback(() => {
        const errors = {};
        if (!formData.Code?.trim()) {
            errors.Code = 'Branch code is required';
        }
        if (!formData.Name?.trim()) {
            errors.Name = 'Branch name is required';
        }
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    }, [formData.Code, formData.Name]);

    const handleChange = useCallback((e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;
        setFormData(prev => ({ ...prev, [name]: newValue }));
        if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    }, [validationErrors]);

    const handleSubmit = () => {
        if (validateForm()) {
            onSave(formData);
        }
    };

    const handleClose = useCallback(() => {
        setValidationErrors({});
        onClose();
    }, [onClose]);

    const isFormValid = !!(formData.Code && formData.Name) && Object.keys(validationErrors).length === 0;

    // Styles
    const sectionHeaderStyle = {
        background: '#1e3a5f',
        color: 'white',
        padding: '8px 14px',
        fontSize: '12px',
        fontWeight: '700',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        borderRadius: '4px',
        marginTop: '16px',
    };

    const labelStyle = {
        display: 'block',
        fontSize: '11px',
        fontWeight: '700',
        color: '#1e293b',
        textTransform: 'uppercase',
        marginBottom: '4px',
    };

    const inputStyle = {
        width: '100%',
        padding: '8px 10px',
        fontSize: '13px',
        border: '1px solid #d1d5db',
        borderRadius: '4px',
        outline: 'none',
        color: '#1e293b',
        fontFamily: 'inherit',
        background: 'white',
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth
            PaperProps={{
                style: {
                    borderRadius: '8px',
                    overflow: 'hidden',
                }
            }}
        >
            {/* Modal Header */}
            <div style={{
                background: '#1e3a5f',
                color: 'white',
                padding: '12px 16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <EditIcon style={{ fontSize: '18px' }} />
                    <span style={{ fontSize: '14px', fontWeight: '700', textTransform: 'uppercase' }}>
                        Edit Branch — {formData.Name}
                    </span>
                </div>
                <IconButton onClick={handleClose} size="small" style={{ color: 'white' }}>
                    <CloseIcon style={{ fontSize: '20px' }} />
                </IconButton>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '0 20px 20px', maxHeight: '70vh', overflowY: 'auto' }}>

                {/* Basic Information */}
                <div style={sectionHeaderStyle}>Basic Information</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Branch Code <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            name="Code"
                            value={formData.Code}
                            onChange={handleChange}
                            maxLength={5}
                            style={{ ...inputStyle, borderColor: validationErrors.Code ? '#ef4444' : '#d1d5db' }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = validationErrors.Code ? '#ef4444' : '#d1d5db'}
                        />
                        {validationErrors.Code && <span style={{ fontSize: '11px', color: '#ef4444' }}>{validationErrors.Code}</span>}
                    </div>
                    <div>
                        <label style={labelStyle}>Branch Name <span style={{ color: '#ef4444' }}>*</span></label>
                        <input
                            name="Name"
                            value={formData.Name}
                            onChange={handleChange}
                            maxLength={150}
                            style={{ ...inputStyle, borderColor: validationErrors.Name ? '#ef4444' : '#d1d5db' }}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                            onBlur={(e) => e.target.style.borderColor = validationErrors.Name ? '#ef4444' : '#d1d5db'}
                        />
                        {validationErrors.Name && <span style={{ fontSize: '11px', color: '#ef4444' }}>{validationErrors.Name}</span>}
                    </div>
                </div>

                {/* Address Details */}
                <div style={sectionHeaderStyle}>Address Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Address Line 1</label>
                        <input name="AddressLine1" value={formData.AddressLine1} onChange={handleChange} maxLength={200} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Address Line 2</label>
                        <input name="AddressLine2" value={formData.AddressLine2} onChange={handleChange} maxLength={200} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>City</label>
                        <input name="Location" value={formData.Location} onChange={handleChange} maxLength={50} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Pin Code</label>
                        <input name="PinCode" value={formData.PinCode} onChange={handleChange} maxLength={6} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>State Code ID</label>
                        <input name="StateCodeId" type="number" value={formData.StateCodeId} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>

                {/* Contact Details */}
                <div style={sectionHeaderStyle}>Contact Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Primary Contact</label>
                        <input name="ContactNumber1" value={formData.ContactNumber1} onChange={handleChange} maxLength={15} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Secondary Contact</label>
                        <input name="ContactNumber2" value={formData.ContactNumber2} onChange={handleChange} maxLength={15} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Alternate Contact</label>
                        <input name="ContactNumber3" value={formData.ContactNumber3} onChange={handleChange} maxLength={15} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>
                <div style={{ marginTop: '12px' }}>
                    <label style={labelStyle}>Email</label>
                    <input name="Email" type="email" value={formData.Email} onChange={handleChange} maxLength={100} style={inputStyle}
                        onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                </div>

                {/* Tax Details */}
                <div style={sectionHeaderStyle}>Tax Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>VAT Number</label>
                        <input name="VATNumber" value={formData.VATNumber} onChange={handleChange} maxLength={15} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>PAN Number</label>
                        <input name="PanNumber" value={formData.PanNumber} onChange={handleChange} maxLength={10} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Importer Code / Customs No</label>
                        <input name="ImporterCode" value={formData.ImporterCode} onChange={handleChange} maxLength={50} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Trade License No</label>
                        <input name="TradeLicenseNo" value={formData.TradeLicenseNo} onChange={handleChange} maxLength={50} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Payment Bank ID</label>
                        <input name="PaymentBankId" type="number" value={formData.PaymentBankId} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Bill Prefix</label>
                        <input name="BillPrefix" value={formData.BillPrefix} onChange={handleChange} maxLength={5} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>

                {/* Bill Prefix Details */}
              {/*  <div style={sectionHeaderStyle}>Bill Prefix Details</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    
                     <div>
                        <label style={labelStyle}>B2C / IGST Cus</label>
                        <input name="BToCPrefix" value={formData.BToCPrefix} onChange={handleChange} maxLength={5} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>IGST Prefix</label>
                        <input name="IGSTPrefix" value={formData.IGSTPrefix} onChange={handleChange} maxLength={5} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>IGST Customer</label>
                        <input name="IGSTCustomerPrefix" value={formData.IGSTCustomerPrefix} onChange={handleChange} maxLength={5} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div> 
                </div>*/}

                {/* Opening Balances */}
                <div style={sectionHeaderStyle}>Opening Balances</div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '12px', marginTop: '12px' }}>
                    <div>
                        <label style={labelStyle}>Cash Opening Balance</label>
                        <input name="CashOpeningBalance" type="number" value={formData.CashOpeningBalance} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Cash Opening Date</label>
                        <input name="CashOpeningDate" type="date" value={formData.CashOpeningDate} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Stock Opening Balance</label>
                        <input name="StockOpeningBalance" type="number" value={formData.StockOpeningBalance} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                    <div>
                        <label style={labelStyle}>Stock Opening Date</label>
                        <input name="StockOpeningDate" type="date" value={formData.StockOpeningDate} onChange={handleChange} style={inputStyle}
                            onFocus={(e) => e.target.style.borderColor = '#3b82f6'} onBlur={(e) => e.target.style.borderColor = '#d1d5db'} />
                    </div>
                </div>


            </div>

            {/* Footer Buttons */}
            <div style={{
                padding: '12px 20px',
                borderTop: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px',
                background: '#f8fafc',
            }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isSaving}
                    style={{
                        borderColor: '#1e3a5f',
                        color: '#1e3a5f',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'none',
                        padding: '6px 20px',
                        borderRadius: '4px',
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={isSaving || !isFormValid}
                    style={{
                        background: '#1e3a5f',
                        color: 'white',
                        fontSize: '12px',
                        fontWeight: '600',
                        textTransform: 'none',
                        padding: '6px 20px',
                        borderRadius: '4px',
                    }}
                >
                    {isSaving ? <CircularProgress size={14} color="inherit" style={{ marginRight: '6px' }} /> : null}
                    {isSaving ? 'Updating...' : 'Update Branch'}
                </Button>
            </div>
        </Dialog>
    );
};

export default BranchEditModal;
